import { useEffect, useState } from 'react';
import { AlertTriangle, Archive, ExternalLink } from 'lucide-react';
import type { PropertyArchivePreflightResponse } from '@/shared/api';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components';
import { NameConfirmation, namesMatch } from '../lifecycle/NameConfirmation';

interface ArchivePropertyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  propertyName: string;
  preflight?: PropertyArchivePreflightResponse;
  loading?: boolean;
  error?: string;
  archiving?: boolean;
  onConfirm: (reason?: string) => void | Promise<void>;
}

export function ArchivePropertyDialog({
  open,
  onOpenChange,
  propertyName,
  preflight,
  loading,
  error,
  archiving,
  onConfirm,
}: ArchivePropertyDialogProps) {
  const [reason, setReason] = useState('');
  const [typed, setTyped] = useState('');

  // Der Dialog lebt in der Layout-Komponente und wird pro Apartment wiederverwendet — ohne das
  // Zuruecksetzen truege die Eingabe des vorigen Apartments hierher, und die Huerde waere keine.
  useEffect(() => {
    if (!open) {
      setTyped('');
      setReason('');
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <div className="bg-sealwax/10 text-sealwax mb-3 flex size-10 items-center justify-center rounded-full">
            <Archive size={18} aria-hidden />
          </div>
          <DialogTitle>„{propertyName}“ archivieren?</DialogTitle>
          <DialogDescription>
            Das Apartment verschwindet aus dem aktiven Betrieb. Akte, Buchungen und Finanzhistorie
            bleiben vollständig lesbar.
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          {loading ? (
            <p className="text-ink-soft text-sm" role="status">
              Voraussetzungen werden geprüft…
            </p>
          ) : error ? (
            <div className="border-sealwax/30 bg-sealwax/5 text-sealwax rounded-md border p-4 text-sm">
              {error}
            </div>
          ) : preflight && !preflight.canArchive ? (
            <div className="space-y-3" aria-live="polite">
              <p className="text-sealwax flex items-center gap-2 text-sm font-semibold">
                <AlertTriangle size={16} aria-hidden />
                Archivierung ist noch nicht möglich
              </p>
              {preflight.blockers.map((blocker) => (
                <div key={blocker.code} className="border-line bg-card-alt rounded-md border p-4">
                  <p className="text-ink text-sm">{blocker.message}</p>
                  {blocker.actionUrl && (
                    <a
                      href={blocker.actionUrl}
                      className="text-sage mt-2 inline-flex items-center gap-1 text-xs font-semibold hover:underline"
                    >
                      Blocker beheben <ExternalLink size={12} aria-hidden />
                    </a>
                  )}
                </div>
              ))}
            </div>
          ) : preflight?.canArchive ? (
            <div className="space-y-4">
              {/*
                Archivieren ist der Endzustand: es schliesst das Apartment beim Kanalmanager und
                nimmt es aus jeder operativen Flaeche. Anders als Pausieren ist es nicht als
                Alltagsaktion gedacht — deshalb steht die Tragweite hier als Warnung und nicht nur
                als Beschreibung im Kopf.
              */}
              <div
                role="alert"
                className="border-sealwax/40 bg-sealwax/10 flex items-start gap-3 rounded-md border p-4"
              >
                <AlertTriangle size={18} className="text-sealwax mt-0.5 shrink-0" aria-hidden />
                <div className="text-ink text-sm">
                  <p className="font-semibold">Das ist der Endzustand dieses Apartments.</p>
                  <p className="text-ink-soft mt-1">
                    Es wird beim Kanalmanager geschlossen, verschwindet aus Kalender, Reinigung und
                    Preisen und nimmt keine Buchungen mehr an. Willst du es nur vorübergehend aus
                    dem Verkauf nehmen, ist <strong>Pausieren</strong> der richtige Weg.
                  </p>
                </div>
              </div>

              <NameConfirmation
                expectedName={propertyName}
                value={typed}
                onChange={setTyped}
                disabled={archiving}
              />

              <label className="text-ink-soft block text-sm">
                Interner Grund <span className="text-ink-faint">(optional)</span>
                <textarea
                  value={reason}
                  maxLength={500}
                  onChange={(event) => setReason(event.target.value)}
                  className="border-line bg-card text-ink mt-2 min-h-20 w-full resize-y rounded-md border px-3 py-2"
                  placeholder="Zum Beispiel: Objekt nicht mehr im Bestand"
                />
              </label>
            </div>
          ) : null}
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={archiving}>
            Abbrechen
          </Button>
          {preflight?.canArchive && !loading && !error && (
            <Button
              variant="destructive"
              loading={archiving}
              disabled={archiving || !namesMatch(propertyName, typed)}
              onClick={() => onConfirm(reason.trim() || undefined)}
            >
              Apartment archivieren
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
